import { useCallback } from "react"
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import { NumberPicker } from "~/react/ui/number-picker"
import { terms_of_service_path } from "~/lib/routes"
import { CardFormDisplay, CardLogo } from "./card_form_display"
import { useAddSlotsForm } from "./use_add_slots_form"
import { formatCurrency, formatDate, formatCardExpiration } from "./utils"
import type { ModalContentProps, CreditCardInfo } from "./constants"
import { Separator } from "@/react/ui/separator"

// Shared form UI component - no hooks, just presentation
type FormUIProps = {
  // State
  extraSlotsQuantity: number
  setExtraSlotsQuantity: (value: number) => void
  isSubmitting: boolean
  isEditingCard: boolean
  cardholderName: string
  setCardholderName: (value: string) => void
  currentCreditCard: CreditCardInfo | null | undefined

  // Computed
  totalPrice: number
  proratedAmount: number
  isCardFormVisible: boolean
  formattedCardExpiration: string | null
  canSubmit: boolean
  minExtraSlots: number
  maxExtraSlots: number

  // Props passthrough
  currentMaxEmailAccounts: number
  pricePerSlot: number
  currencySymbol: string
  currencyFormat: string
  nextBillingDate?: string | null
  billingCycle: string

  // Handlers
  onClose: () => void
  onSubmit: (e: React.FormEvent) => void
  onEditPaymentMethod: () => void
  onCancelEditCard: () => void

  // Card form options
  showCardElement: boolean
}

function FormUI({
  extraSlotsQuantity,
  setExtraSlotsQuantity,
  isSubmitting,
  isEditingCard,
  cardholderName,
  setCardholderName,
  currentCreditCard,
  totalPrice,
  proratedAmount,
  isCardFormVisible,
  formattedCardExpiration,
  canSubmit,
  minExtraSlots,
  maxExtraSlots,
  currentMaxEmailAccounts,
  pricePerSlot,
  currencySymbol,
  currencyFormat,
  nextBillingDate,
  billingCycle,
  onClose,
  onSubmit,
  onEditPaymentMethod,
  onCancelEditCard,
  showCardElement,
}: FormUIProps) {
  return (
    <form className="upgrade-modal__main" onSubmit={onSubmit}>
      <div className="upgrade-modal__header">
        <div className="upgrade-modal__heading">
          <h2 className="upgrade-modal__title">{i18n.t("js.email_accounts_center.add_slots.title")}</h2>
        </div>
        <p
          className="upgrade-modal__description"
          dangerouslySetInnerHTML={{
            __html: i18n.t("js.email_accounts_center.add_slots.description_html", {
              current: currentMaxEmailAccounts,
              price: formatCurrency(pricePerSlot, currencySymbol, currencyFormat),
            }),
          }}
        />

        <Button
          variant="ghost"
          iconOnly
          className="upgrade-modal__close"
          aria-label={i18n.t("js.cancel")}
          type="button"
          onClick={onClose}
        >
          <Icon icon="times" variant="regular" aria-hidden="true" />
        </Button>
      </div>

      <div className="upgrade-modal__body">
        <div className="upgrade-modal-auto-purchase__form mt-0">
          <label className="upgrade-modal__subtitle mb-2" htmlFor="additional-slots">
            {i18n.t("js.email_accounts_center.add_slots.subtitle")}
          </label>
          <div className="upgrade-modal-auto-purchase__form-limit">
            <NumberPicker
              size="sm"
              value={extraSlotsQuantity}
              onChange={setExtraSlotsQuantity}
              min={minExtraSlots}
              max={maxExtraSlots}
              ariaLabelPrefix={i18n.t("js.email_accounts_center.add_slots.subtitle")}
            />

            <div className="upgrade-modal-auto-purchase__form-info">
              <span
                dangerouslySetInnerHTML={{
                  __html: i18n.t("js.email_accounts_center.add_slots.info_html", {
                    slots: extraSlotsQuantity,
                    price: formatCurrency(totalPrice, currencySymbol, currencyFormat),
                  }),
                }}
              />
            </div>
          </div>
        </div>

        <Separator variant="light" className="my-5" />

        {/* Payment method section */}
        <div className="upgrade-modal-payment-method">
          <h3 className="upgrade-modal__subtitle mt-5 mb-3">
            {i18n.t("js.email_accounts_center.add_slots.payment_method")}
          </h3>

          {/* Current card display */}
          {!isEditingCard && currentCreditCard && (
            <div className="current-card">
              <CardLogo cardType={currentCreditCard.card_type} />
              <span className="current-card__details">
                {currentCreditCard.card_type} **** {currentCreditCard.last_4}
                {formattedCardExpiration && (
                  <span className="text-hint text-xs">
                    {" "}
                    · {i18n.t("js.expires")} {formattedCardExpiration}
                  </span>
                )}
              </span>
              <Button variant="linkGrey" size="sm" className="ml-auto" type="button" onClick={onEditPaymentMethod}>
                {i18n.t("js.email_accounts_center.add_slots.edit")}
              </Button>
            </div>
          )}

          {/* New card form */}
          {isCardFormVisible && (
            <CardFormDisplay
              cardholderName={cardholderName}
              setCardholderName={setCardholderName}
              onCancel={onCancelEditCard}
              showBackButton={!!currentCreditCard}
              showCardElement={showCardElement}
            />
          )}
        </div>

        {/* Total due today section */}
        <dl className="upgrade-modal-summary">
          <div className="upgrade-modal-summary__item">
            <dt className="upgrade-modal-summary__label">
              {i18n.t("js.email_accounts_center.add_slots.total_due_today")}
            </dt>
            <dd className="upgrade-modal-summary__value">
              {formatCurrency(proratedAmount, currencySymbol, currencyFormat)}
            </dd>
          </div>
        </dl>

        {nextBillingDate && proratedAmount !== totalPrice && (
          <p className="h-hint mt-2">
            {i18n.t(`js.email_accounts_center.add_slots.prorated_text.${billingCycle}`, {
              date: formatDate(nextBillingDate),
              price: formatCurrency(
                billingCycle === "yearly" ? totalPrice * 12 : totalPrice,
                currencySymbol,
                currencyFormat,
              ),
            })}
          </p>
        )}
      </div>

      <div className="upgrade-modal__footer">
        <Button variant="primary" fullWidth type="submit" disabled={!canSubmit}>
          {isSubmitting ? i18n.t("js.loading") : i18n.t("js.email_accounts_center.add_slots.cta")}
        </Button>
        <p
          className="mt-2 h-hint"
          dangerouslySetInnerHTML={{
            __html: i18n.t("js.email_accounts_center.add_slots.terms_html", {
              terms_url: terms_of_service_path(),
            }),
          }}
        />
      </div>
    </form>
  )
}

// Modal content with Stripe support (must be wrapped in Elements provider)
export function ModalContent({
  onClose,
  currentEmailAccountsCount,
  currentMaxEmailAccounts,
  absoluteMaxEmailAccounts,
  planIncludedEmailAccounts,
  pricePerSlot,
  currencySymbol,
  currencyFormat,
  billingCycle,
  subscriptionLevel,
  creditCardInfo,
  nextBillingDate,
  billingPeriodStart,
  billingInfo,
  userEmail,
  initialCardholderName,
  dialogRef,
}: ModalContentProps) {
  const stripe = useStripe()
  const elements = useElements()

  // Calculate maximum extra slots allowed
  const maxExtraSlots = absoluteMaxEmailAccounts - planIncludedEmailAccounts

  const {
    extraSlotsQuantity,
    setExtraSlotsQuantity,
    isSubmitting,
    setIsSubmitting,
    isEditingCard,
    cardholderName,
    setCardholderName,
    currentCreditCard,
    setCurrentCreditCard,
    totalPrice,
    proratedAmount,
    isCardFormVisible,
    minExtraSlots,
    handleEditPaymentMethod,
    handleCancelEditCard,
    saveCreditCard,
    updateSubscription,
    handleSuccessfulSubmit,
  } = useAddSlotsForm({
    currentEmailAccountsCount,
    currentMaxEmailAccounts,
    planIncludedEmailAccounts,
    pricePerSlot,
    nextBillingDate,
    billingPeriodStart,
    initialCardholderName,
    creditCardInfo,
    billingCycle,
    subscriptionLevel,
    billingInfo,
    userEmail,
    onClose,
  })

  const handleClose = useCallback(() => {
    dialogRef.current?.close()
  }, [dialogRef])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (isSubmitting) return

      setIsSubmitting(true)

      try {
        // If editing card, first create payment method and save it
        if (isEditingCard || !currentCreditCard) {
          if (!stripe || !elements) {
            window.App?.displayFlash?.(i18n.t("js.email_accounts_center.add_slots.stripe_loading"), "error")
            setIsSubmitting(false)
            return
          }

          const cardElement = elements.getElement(CardElement)
          if (!cardElement) {
            window.App?.displayFlash?.(i18n.t("js.email_accounts_center.add_slots.card_error"), "error")
            setIsSubmitting(false)
            return
          }

          // Build billing details
          const billingDetails: {
            name: string
            email?: string
            address?: {
              line1: string
              city: string
              postal_code: string
              country: string
            }
          } = {
            name: cardholderName,
            email: userEmail || undefined,
          }

          if (billingInfo) {
            billingDetails.address = {
              line1: billingInfo.address || "",
              city: billingInfo.city || "",
              postal_code: billingInfo.postal_code || "",
              country: billingInfo.country_code || "",
            }
          }

          // Step 1: Create payment method with Stripe
          const result = await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
            billing_details: billingDetails,
          })

          if (result.error) {
            window.App?.displayFlash?.(result.error.message || "An error occurred", "error")
            setIsSubmitting(false)
            return
          }

          // Step 2: Save credit card to server
          const cardSaved = await saveCreditCard({
            paymentMethodId: result.paymentMethod.id,
            cardholderName,
          })
          if (!cardSaved) {
            setIsSubmitting(false)
            return
          }

          // Update local state with new card info
          const pm = result.paymentMethod
          setCurrentCreditCard({
            card_type: pm.card?.brand || "Card",
            last_4: pm.card?.last4 || "****",
            expiration_month: pm.card?.exp_month || null,
            expiration_year: pm.card?.exp_year?.toString() || null,
          })
        }

        // Step 3: Update subscription (change email accounts number)
        // Convert extra slots to total slots for API
        const totalDesiredSlots = planIncludedEmailAccounts + extraSlotsQuantity
        const subscriptionUpdated = await updateSubscription(totalDesiredSlots)

        if (subscriptionUpdated) {
          handleSuccessfulSubmit()
        }
      } finally {
        setIsSubmitting(false)
      }
    },
    [
      isSubmitting,
      isEditingCard,
      currentCreditCard,
      stripe,
      elements,
      cardholderName,
      userEmail,
      billingInfo,
      saveCreditCard,
      setCurrentCreditCard,
      updateSubscription,
      planIncludedEmailAccounts,
      extraSlotsQuantity,
      handleSuccessfulSubmit,
      setIsSubmitting,
    ],
  )

  const formattedCardExpiration = formatCardExpiration(
    currentCreditCard?.expiration_month,
    currentCreditCard?.expiration_year,
  )

  const canSubmit = !isSubmitting && !!(currentCreditCard || (stripe && elements && cardholderName.trim().length > 0))

  return (
    <FormUI
      extraSlotsQuantity={extraSlotsQuantity}
      setExtraSlotsQuantity={setExtraSlotsQuantity}
      isSubmitting={isSubmitting}
      isEditingCard={isEditingCard}
      cardholderName={cardholderName}
      setCardholderName={setCardholderName}
      currentCreditCard={currentCreditCard}
      totalPrice={totalPrice}
      proratedAmount={proratedAmount}
      isCardFormVisible={isCardFormVisible}
      formattedCardExpiration={formattedCardExpiration}
      canSubmit={canSubmit}
      minExtraSlots={minExtraSlots}
      maxExtraSlots={maxExtraSlots}
      currentMaxEmailAccounts={currentMaxEmailAccounts}
      pricePerSlot={pricePerSlot}
      currencySymbol={currencySymbol}
      currencyFormat={currencyFormat}
      nextBillingDate={nextBillingDate}
      billingCycle={billingCycle}
      onClose={handleClose}
      onSubmit={handleSubmit}
      onEditPaymentMethod={handleEditPaymentMethod}
      onCancelEditCard={handleCancelEditCard}
      showCardElement={!!stripe && !!elements}
    />
  )
}

// Modal content without Stripe support (fallback when no Stripe key)
export function ModalContentWithoutStripe({
  onClose,
  currentEmailAccountsCount,
  currentMaxEmailAccounts,
  absoluteMaxEmailAccounts,
  planIncludedEmailAccounts,
  pricePerSlot,
  currencySymbol,
  currencyFormat,
  billingCycle,
  subscriptionLevel,
  creditCardInfo,
  nextBillingDate,
  billingPeriodStart,
  initialCardholderName,
  dialogRef,
}: Omit<ModalContentProps, "billingInfo" | "userEmail">) {
  // Calculate maximum extra slots allowed
  const maxExtraSlots = absoluteMaxEmailAccounts - planIncludedEmailAccounts

  const {
    extraSlotsQuantity,
    setExtraSlotsQuantity,
    isSubmitting,
    setIsSubmitting,
    isEditingCard,
    cardholderName,
    setCardholderName,
    currentCreditCard,
    totalPrice,
    proratedAmount,
    isCardFormVisible,
    minExtraSlots,
    handleEditPaymentMethod,
    handleCancelEditCard,
    updateSubscription,
    handleSuccessfulSubmit,
  } = useAddSlotsForm({
    currentEmailAccountsCount,
    currentMaxEmailAccounts,
    planIncludedEmailAccounts,
    pricePerSlot,
    nextBillingDate,
    billingPeriodStart,
    initialCardholderName,
    creditCardInfo,
    billingCycle,
    subscriptionLevel,
    onClose,
  })

  const handleClose = useCallback(() => {
    dialogRef.current?.close()
  }, [dialogRef])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (isSubmitting || !currentCreditCard) return

      setIsSubmitting(true)

      try {
        // Convert extra slots to total slots for API
        const totalDesiredSlots = planIncludedEmailAccounts + extraSlotsQuantity
        const subscriptionUpdated = await updateSubscription(totalDesiredSlots)

        if (subscriptionUpdated) {
          handleSuccessfulSubmit()
        }
      } finally {
        setIsSubmitting(false)
      }
    },
    [
      isSubmitting,
      currentCreditCard,
      setIsSubmitting,
      updateSubscription,
      planIncludedEmailAccounts,
      extraSlotsQuantity,
      handleSuccessfulSubmit,
    ],
  )

  const formattedCardExpiration = formatCardExpiration(
    currentCreditCard?.expiration_month,
    currentCreditCard?.expiration_year,
  )

  const canSubmit = !isSubmitting && !!currentCreditCard && !isEditingCard

  return (
    <FormUI
      extraSlotsQuantity={extraSlotsQuantity}
      setExtraSlotsQuantity={setExtraSlotsQuantity}
      isSubmitting={isSubmitting}
      isEditingCard={isEditingCard}
      cardholderName={cardholderName}
      setCardholderName={setCardholderName}
      currentCreditCard={currentCreditCard}
      totalPrice={totalPrice}
      proratedAmount={proratedAmount}
      isCardFormVisible={isCardFormVisible}
      formattedCardExpiration={formattedCardExpiration}
      canSubmit={canSubmit}
      minExtraSlots={minExtraSlots}
      maxExtraSlots={maxExtraSlots}
      currentMaxEmailAccounts={currentMaxEmailAccounts}
      pricePerSlot={pricePerSlot}
      currencySymbol={currencySymbol}
      currencyFormat={currencyFormat}
      nextBillingDate={nextBillingDate}
      billingCycle={billingCycle}
      onClose={handleClose}
      onSubmit={handleSubmit}
      onEditPaymentMethod={handleEditPaymentMethod}
      onCancelEditCard={handleCancelEditCard}
      showCardElement={false}
    />
  )
}
